// ============================================================
// TMR Portal Admin — publicar uma versão: o que muda, e a nota que fica na história
// ============================================================
import { useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { ModalShell } from '../../components/ModalShell';
import { Notice } from '../../components/Notice';
import { Skeleton } from '../../components/states';
import { useTemplateDiff } from '../../data/contractTemplates/queries';
import type { ContractTemplateDetail } from '../../data/contractTemplates/contract';
import { TemplateDiffView } from './TemplateDiffView';

/** Props for {@link PublishVersionModal}. */
interface PublishVersionModalProps {
  template: ContractTemplateDetail;
  /** The draft being published. */
  draftVersionId: string;
  /** What stops it from being published, as the preview reported it. Empty when it may be. */
  problems: string[];
  /** Whether the editor still holds text that was not saved. */
  unsaved: boolean;
  publishing: boolean;
  error: unknown;
  onPublish: (note: string) => void;
  onClose: () => void;
}

/**
 * The last screen before the text every patient of a clinic signs changes.
 *
 * It shows the comparison rather than asking for confidence: publishing is irreversible in the sense that
 * matters — the previous version is superseded, contracts issued from it keep it, and the only way back is
 * to publish again. Seeing what moved is the check that catches a paste in the wrong place.
 *
 * **The note is required and is not ceremony.** It is the only account of why the text changed on a given
 * day, and it is what the timeline shows on every Empresa (`24` §10).
 */
export function PublishVersionModal({
  template,
  draftVersionId,
  problems,
  unsaved,
  publishing,
  error,
  onPublish,
  onClose,
}: PublishVersionModalProps) {
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);

  // Compared with the version in force by default — the only comparison that answers "what am I about to
  // change". A template publishing for the first time has nothing to compare against, and says so.
  const diff = useTemplateDiff(template.id, draftVersionId);

  const firstPublication = template.currentVersionId === null;
  const blocked = problems.length > 0 || unsaved;

  return (
    <ModalShell
      title="Publicar esta versão"
      width={720}
      lead={
        template.companyId === null
          ? 'A partir de agora, todo contrato deste tipo passa a ser emitido com este texto.'
          : 'A partir de agora, os contratos desta clínica passam a ser emitidos com este texto.'
      }
      onClose={onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {unsaved && (
          <Notice tone="warning">
            Há alterações não salvas. Salve o rascunho antes de publicar — o que será publicado é o texto
            gravado, não o que está na tela.
          </Notice>
        )}

        {problems.length > 0 && (
          <Notice tone="danger">
            <strong>Este texto ainda não pode ser publicado:</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </Notice>
        )}

        {firstPublication ? (
          <Notice tone="neutral">
            Esta é a primeira versão deste modelo — não há versão anterior com que comparar.
          </Notice>
        ) : diff.isPending ? (
          <Skeleton rows={4} />
        ) : diff.isError ? (
          <Notice tone="warning">
            Não foi possível carregar as diferenças. Você ainda pode publicar, mas sem ver o que muda.
          </Notice>
        ) : diff.data.identical ? (
          <Notice tone="warning">
            O texto é idêntico ao da versão {diff.data.fromVersionNumber}. Publicar criaria uma versão nova sem
            nenhuma mudança.
          </Notice>
        ) : (
          <TemplateDiffView diff={diff.data} maxHeight={280} />
        )}

        <div>
          <label className="pa-form-label" htmlFor="publication-note">
            O que muda nesta versão <span style={{ color: 'var(--danger)' }}>*</span>
          </label>
          <textarea
            id="publication-note"
            className="pa-input"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Ex.: ajusta a cláusula de cancelamento pedida pelo jurídico."
            style={{
              width: '100%',
              padding: '9px 12px',
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-sm)',
              resize: 'vertical',
            }}
          />
          {touched && !note.trim() && (
            <div style={{ marginTop: 4, fontSize: 12.5, color: 'var(--danger)' }}>
              Descreva o que muda — é o que a história do modelo mostra.
            </div>
          )}
        </div>

        {error != null && (
          <Notice tone="danger">
            {error instanceof ApiError ? error.detail : 'Não foi possível publicar a versão.'}
          </Notice>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
          <Btn variant="secondary" onClick={onClose}>
            Cancelar
          </Btn>
          <Btn
            variant="primary"
            disabled={publishing || blocked}
            onClick={() => {
              setTouched(true);
              if (note.trim()) onPublish(note.trim());
            }}
          >
            {publishing ? 'Publicando…' : 'Publicar versão'}
          </Btn>
        </div>
      </div>
    </ModalShell>
  );
}
